import { JSX, ReactNode } from 'react';

import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { Link } from 'react-router-dom';

import { isDeviceTypeMobile } from '@shared/utils';
import { InteractiveElement } from '@shared/components';

import { Button, Drawer } from '@/widgets';
import { CrossIcon } from '@/assets/icons';
import {
	PN_NOTIF_CLICK,
	TAKE_NPS_SURVEY_CLICKED,
	PUSH_NOTIFICATION_DRAWER_CLOSED,
} from '@/constants';
import { sendClickEvent } from '@/utils/AnalyticsUtils';
import {
	IMessageActionType,
	IMessageData,
	IMessageType,
	INotificationState,
} from '@/types';

import styles from './NotificationsDrawer.module.scss';
import { useNotificationsDrawer } from './useNotificationsDrawer';

interface INotificationsDrawerProps {
	handleDrawerClose: () => void;
	isOpen: boolean;
	notifications: INotificationState;
}

const DEVICE_WIDTH = isDeviceTypeMobile() ? '100%' : 400;

const NotificationsDrawer = ({
	handleDrawerClose,
	isOpen = false,
	notifications,
}: INotificationsDrawerProps): JSX.Element => {
	const {
		readNotifications,
		unreadNotifications,
		totalMessageCount,
		merchantType,
		getNotificationLink,
		handleBrickbatNotification,
	} = useNotificationsDrawer(notifications);

	const getNotificationAction = (notification: IMessageData): JSX.Element => {
		if (
			notification.type === IMessageType.ACTION &&
			notification.actionType === IMessageActionType.BRICKBAT_SURVEY
		) {
			return (
				<Button
					className={styles.cardCta}
					onClick={() => {
						handleBrickbatNotification(notification.link);
						handleDrawerClose();
					}}
					variant="link"
					eventData={{
						name: TAKE_NPS_SURVEY_CLICKED,
						value: notification.link,
					}}
				>
					Take Survey
				</Button>
			);
		}

		const notificationLink =
			notification.link && getNotificationLink(notification.link);
		return notificationLink ? (
			<Link onClick={handleDrawerClose} to={notificationLink}>
				<span className={styles.cardCta}>View Details</span>
			</Link>
		) : (
			<></>
		);
	};

	const commonMessageTemplate = (
		type: 'read' | 'unread',
		messages: IMessageData[] = [],
	) => {
		return messages.map((notification) => {
			return (
				<InteractiveElement
					type="div"
					key={notification.id}
					className={cx(
						styles.notificationCard,
						type === 'unread' && styles.isNew,
					)}
					onClick={() => {
						sendClickEvent({
							name: PN_NOTIF_CLICK,
							additionalData: {
								type,
								merchantType: merchantType,
								nav: notification.link,
							},
						});
					}}
				>
					{notification.icon ? (
						<img
							className={styles.cardIcon}
							src={notification.icon}
							alt="message icon"
						/>
					) : null}
					<div>
						<div className={styles.titleWrapper}>
							<div className={styles.cardTitle}>{notification.title}</div>
						</div>
						<div className={styles.cardMessage}>{notification.subTitle}</div>
						<div className={styles.cardMessage}>{notification.message}</div>
						<div className={styles.cardDate}>{notification.date}</div>
						{getNotificationAction(notification)}
					</div>
				</InteractiveElement>
			);
		});
	};

	const readMessageTemplate = (): ReactNode => {
		if (isEmpty(readNotifications)) return null;

		const template = commonMessageTemplate('read', readNotifications);

		return (
			<div className={styles.readSection}>
				<div className={styles.statusTitle}>Read</div>
				<div>{template} </div>
			</div>
		);
	};

	const unreadMessageTemplate = (): ReactNode => {
		if (isEmpty(unreadNotifications)) return null;

		const template = commonMessageTemplate('unread', unreadNotifications);

		return (
			<div className={styles.unreadSection}>
				<div className={styles.statusTitle}>
					<span>Unread</span>
					<span className={styles.messageCount}>
						{unreadNotifications.length}
					</span>
				</div>
				<div>{template} </div>
			</div>
		);
	};

	const onDrawerClose = () => {
		handleDrawerClose();
	};

	return (
		<Drawer
			closable={false}
			width={DEVICE_WIDTH}
			placement="right"
			onClose={onDrawerClose}
			open={isOpen}
			className={styles.drawerRoot}
		>
			<div className={styles.header}>
				<div className={styles.drawerTitle}>Notifications</div>
				<Button
					variant="text"
					className={styles.crossButton}
					onClick={onDrawerClose}
				>
					<CrossIcon
						width={24}
						height={24}
						onClick={() => {
							sendClickEvent({
								name: PUSH_NOTIFICATION_DRAWER_CLOSED,
							});
						}}
					/>
				</Button>
			</div>
			<div className={styles.messagesWrapper}>
				{totalMessageCount > 0 ? (
					<>
						<div>{unreadMessageTemplate()}</div>
						<div className={styles.notificationsWrapperRead}>
							{readMessageTemplate()}
						</div>
					</>
				) : (
					<div className={styles.noMessageScreen}>No new notifications </div>
				)}
			</div>
		</Drawer>
	);
};

export default NotificationsDrawer;
